// Persisted record shapes. ENGINE_DESIGN.md §1 (HandRecord), ARCHITECTURE.md §6 (D1 rows).
import { z } from 'zod';
import { CardIndexSchema, BoardSchema } from './primitives';
import { PositionSchema, StreetSchema } from './position';
import { ActionSchema } from './action';
import { ArchetypeIdSchema } from './archetype';
import { StackDepthBucketSchema } from './buckets';
import { ScenarioSpecSchema, ModeIdSchema, GameTypeSchema, TournamentStageSchema, } from './scenario';
import { EngineFactsSchema, UserActionSchema, HeroImageStateSchema } from './facts';
export const HandSourceSchema = z.enum(['generated', 'imported', 'yardstick']);
export const ShowdownEntrySchema = z.object({
    seat: z.number().int().nonnegative(),
    holeCards: z.tuple([CardIndexSchema, CardIndexSchema]),
    handRank: z.number().int().optional(),
});
export const ResultRecordSchema = z.object({
    winners: z.array(z.number().int().nonnegative()),
    potsAwarded: z.array(z.object({ amount: z.number().nonnegative(), seats: z.array(z.number().int().nonnegative()) })),
    showdown: z.array(ShowdownEntrySchema).optional(),
});
/** Enough to replay the entire hand deterministically (ENGINE_DESIGN.md §1). */
export const HandRecordSchema = z.object({
    spec: ScenarioSpecSchema,
    seed: z.string(),
    actions: z.array(ActionSchema),
    board: BoardSchema,
    holeCards: z.tuple([CardIndexSchema, CardIndexSchema]),
    result: ResultRecordSchema,
    perDecisionFacts: z.array(EngineFactsSchema),
    source: HandSourceSchema.optional(),
});
/** End-of-hand debrief (ARCHITECTURE.md §6 hands.debrief). */
export const DebriefRecordSchema = z.object({
    biggestMistake: z.string().optional(),
    bestDecision: z.string().optional(),
    altLines: z.array(z.string()).optional(),
    summary: z.string().optional(),
});
/** One graded decision point - the row the progress engine lives on (ARCHITECTURE.md §6 decisions). */
export const DecisionRecordSchema = z.object({
    id: z.string().optional(),
    handId: z.string(),
    userId: z.string().optional(),
    street: StreetSchema,
    decisionIndex: z.number().int().nonnegative(),
    // denormalized filter columns:
    position: PositionSchema,
    villainArchetype: ArchetypeIdSchema.optional(),
    stackDepthBucket: StackDepthBucketSchema,
    tournamentStage: TournamentStageSchema.optional(),
    // payloads:
    engineFacts: EngineFactsSchema,
    userAction: UserActionSchema,
    grade: z.number().min(1).max(10),
    evLossBB: z.number().nullable().optional(),
    skillScores: z.record(z.string(), z.number()),
    errorCodes: z.array(z.string()),
    firedRuleIds: z.array(z.string()),
    createdAt: z.number().int().nonnegative().optional(),
});
/** One training sitting (ARCHITECTURE.md §6 sessions). */
export const SessionRecordSchema = z.object({
    id: z.string().optional(),
    userId: z.string().optional(),
    mode: ModeIdSchema,
    gameType: GameTypeSchema,
    stakesLabel: z.string(),
    imageState: HeroImageStateSchema.optional(),
    startedAt: z.number().int().nonnegative(),
    endedAt: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=records.js.map